import React from 'react';

import {assert} from 'Asserts';
import {pathToURL} from 'BenchlingUtil';
import * as SampleConstants from 'constants/SampleConstants';
import BenchLink from 'link-preview/BenchLink';
import {
  ColumnDefinition,
  schemaFieldToColumnDefinition,
} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {Cells, ColumnConstraint, Table, TableColumnType} from 'notebook/tables/TableConstants';
import {convertTagToTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType} from 'notebook/tables/typed-values/TypedValueConstants';
import {FulfillmentStatus} from 'requests/fulfillments/FulfillmentConstants';
import * as FulfillmentTables from 'requests/fulfillments/FulfillmentTables';
import {HydratedFulfillmentWithPreparedSampleGroup} from 'requests/fulfillments/HydratedFulfillmentConstants';
import {RequestSchema} from 'requests/RequestConstants';
import * as RequestDAO from 'requests/RequestDAO';
import {
  getGridPositionTableCell,
  getSampleGroupSampleTableCell,
  getStorableTableCell,
} from 'requests/RequestTableCells';
import {
  getContainerColumnsForSampleGroupField,
  getContainerForSampleFromREST,
  getSampleForField,
} from 'requests/SampleGroupUtil';
import {RequestTaskSchema} from 'requests/tasks/RequestTaskConstants';
import {getParentStorableFromAncestralStorables} from 'samples/storables/ParentStorableUtil';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {findSchemaTag} from 'schemas/tags/TagUtil';
import {PreparedSampleGroup} from 'workflows/samples/PreparedSampleGroupConstants';
import {SampleGroupSchema} from 'workflows/WorkflowConstants';

import {StorableSummaryGraphQL} from '../../graphql-fragments/inventory/__generated/StorableSummaryGraphQL';

/**
 * The main export from this file prepares the essential table data needed to represent
 * a request fulfillments table.
 *
 * It requires quite a bit of raw data (see CreateRequestFulfillmentsTableParams), and converts
 * them into a full Table object.
 *
 * We also export `getColumnDefinitions()`. This is used internally, but can also be used to
 * inspect the column definitions outside of table rendering.
 */

// Column constraint with required columnDefinition and renderedLabel
interface FulfillmentSpreadsheetColumn extends ColumnConstraint {
  columnDefinition: ColumnDefinition;
  renderedLabel: string;
}

interface CreateRequestFulfillmentsTableParams {
  hydratedFulfillments: Array<HydratedFulfillmentWithPreparedSampleGroup>;
  containersById: {[containerId: string]: SampleConstants.Container | null};
  ancestralStorablesById: Record<string, Array<StorableSummaryGraphQL>>;
  // Determines if we should lock the Marked For Redo cell as all other cells
  // will always be locked for the RFT
  locked: boolean;

  // Determines if each row should be marked as isFinalized. In the Entry, we
  // want this to be false as that allows the user to remove rows from the
  // RequestFulfillmentTable. However, we need this to be true in the case of
  // the InputTablesSampleSourceCollector for the AddSamples modal.
  isFinalized: boolean;
}

function getSampleGroupSchemas(
  hydratedFulfillments: Array<HydratedFulfillmentWithPreparedSampleGroup>
): Array<SampleGroupSchema> {
  const sampleGroupSchemas = [];
  const sampleGroupSchemaIdSet = new Set();
  for (const hydratedFulfillment of hydratedFulfillments) {
    if (hydratedFulfillment.requestSchema.sampleGroupSchema) {
      const sampleGroupSchema = hydratedFulfillment.requestSchema.sampleGroupSchema;
      if (!sampleGroupSchemaIdSet.has(sampleGroupSchema.id)) {
        sampleGroupSchemas.push(sampleGroupSchema);
        sampleGroupSchemaIdSet.add(sampleGroupSchema.id);
      }
    }
  }
  return sampleGroupSchemas;
}

function getSampleGroups(
  hydratedFulfillments: Array<HydratedFulfillmentWithPreparedSampleGroup>
): Array<PreparedSampleGroup> {
  const sampleGroups: Array<PreparedSampleGroup> = [];
  for (const hydratedFulfillment of hydratedFulfillments) {
    if (hydratedFulfillment.sampleGroup) {
      sampleGroups.push(hydratedFulfillment.sampleGroup as PreparedSampleGroup);
    }
  }
  return sampleGroups;
}

function getContainerFieldColumnsForSampleGroupField(
  hydratedFulfillments: Array<HydratedFulfillmentWithPreparedSampleGroup>,
  sampleGroupField: SchemaField,
  containersById: {[containerId: string]: SampleConstants.Container | null}
): Array<FulfillmentSpreadsheetColumn> {
  const columns: Array<FulfillmentSpreadsheetColumn> = [];
  const containerFieldIdSet = new Set();
  for (const hydratedFulfillment of hydratedFulfillments) {
    const sample = getSampleForField(hydratedFulfillment.sampleGroup, sampleGroupField);
    if (sample && sample.containerId) {
      const container = containersById[sample.containerId];
      assert(container != null, 'Expected containers to be loaded', {sample});
      for (const schemaField of container.storableSchema.fields) {
        if (!containerFieldIdSet.has(schemaField.apiId)) {
          columns.push({
            renderedLabel: schemaField.displayName,
            columnDefinition: schemaFieldToColumnDefinition(schemaField),
          });
          containerFieldIdSet.add(schemaField.apiId);
        }
      }
    }
  }
  return columns;
}

function getSampleFieldContainerColumns(params: Omit<CreateRequestFulfillmentsTableParams, 'isFinalized'>): {
  [sampleGroupFieldId: string]: Array<FulfillmentSpreadsheetColumn>;
} {
  const {hydratedFulfillments, containersById, ancestralStorablesById} = params;
  const sampleGroupSchemas = getSampleGroupSchemas(hydratedFulfillments);
  const sampleGroups = getSampleGroups(hydratedFulfillments);

  const sampleFieldContainerColumns: {
    [sampleGroupFieldId: string]: Array<FulfillmentSpreadsheetColumn>;
  } = {};
  for (const sampleGroupSchema of sampleGroupSchemas) {
    for (const sampleGroupField of sampleGroupSchema.fields) {
      const containerColumnConstraints = getContainerColumnsForSampleGroupField({
        sampleGroups,
        sampleGroupField,
        containersById,
        ancestralStorablesById,
      });
      const containerFieldColumns = getContainerFieldColumnsForSampleGroupField(
        hydratedFulfillments,
        sampleGroupField,
        containersById
      );
      sampleFieldContainerColumns[sampleGroupField.apiId] = [
        ...containerColumnConstraints,
        ...containerFieldColumns,
      ];
    }
  }
  return sampleFieldContainerColumns;
}

function renderSampleColumns({
  hydratedFulfillment,
  sampleGroupSchemaId,
  sampleGroupField,
  containersById,
  ancestralStorablesById,
}: {
  hydratedFulfillment: HydratedFulfillmentWithPreparedSampleGroup;
  sampleGroupSchemaId: string;
  sampleGroupField: SchemaField;
  containersById: {[containerId: string]: SampleConstants.Container | null};
  ancestralStorablesById: Record<string, Array<StorableSummaryGraphQL>>;
}): {
  sampleCell: Cell;
  containerCell: Cell;
  locationCell: Cell;
  positionCell: Cell;
} {
  const noSample = {
    sampleCell: {text: '', locked: true},
    containerCell: {text: '', locked: true},
    locationCell: {text: '', locked: true},
    positionCell: {text: '', locked: true},
  };
  if (sampleGroupSchemaId !== hydratedFulfillment.requestSchema.sampleGroupSchema!.id) {
    return noSample;
  }
  const sample = getSampleForField(hydratedFulfillment.sampleGroup, sampleGroupField);
  if (!sample) {
    return noSample;
  }
  const container = getContainerForSampleFromREST({sample, containersById});
  const location =
    container && ancestralStorablesById[container.id]
      ? getParentStorableFromAncestralStorables(container, ancestralStorablesById[container.id])
      : null;
  let gridCell: SampleConstants.GridCellSummary | null = null;
  if (container) {
    gridCell = container.gridCell || null;
  }
  return {
    sampleCell: getSampleGroupSampleTableCell(sample),
    containerCell: getStorableTableCell(container),
    locationCell: getStorableTableCell(location),
    positionCell: getGridPositionTableCell(gridCell),
  };
}

function renderContainerFieldCell(
  container: SampleConstants.Container | null,
  containerField: SchemaField
): Cell {
  if (!container) {
    return {text: '', locked: true};
  }
  if (!container.storableSchema.fields.some(({apiId}) => apiId === containerField.apiId)) {
    // Container field does not match container's schema
    return {text: '', locked: true};
  }
  const containerTag = findSchemaTag(container.tags, containerField.id);
  if (!containerTag) {
    return {text: '', locked: true};
  }
  return {text: '', typedValue: convertTagToTypedValue(containerTag), locked: true};
}

function createTable(params: CreateRequestFulfillmentsTableParams): Cells {
  const {hydratedFulfillments, containersById, ancestralStorablesById, locked} = params;
  const sampleFieldContainerColumns = getSampleFieldContainerColumns(params);

  return hydratedFulfillments.map((hydratedFulfillment) => {
    const request = hydratedFulfillment.request;
    return FulfillmentTables.fulfillmentRow<Cell>(hydratedFulfillments, {
      renderSampleGroupField: (
        sampleGroupField: SchemaField,
        sampleGroupSchema: SampleGroupSchema
      ): Array<Cell> => {
        FulfillmentTables.checkFulfillmentForRenderSampleGroupField(hydratedFulfillment);
        const {sampleCell, containerCell, locationCell, positionCell} = renderSampleColumns({
          hydratedFulfillment,
          sampleGroupSchemaId: sampleGroupSchema.id,
          sampleGroupField,
          containersById,
          ancestralStorablesById,
        });
        return [
          sampleCell,
          ...(() => {
            const sample = getSampleForField(hydratedFulfillment.sampleGroup, sampleGroupField);
            const containerColumnCells = [];
            for (const {columnDefinition} of sampleFieldContainerColumns[sampleGroupField.apiId]) {
              switch (columnDefinition.type) {
                case TableColumnType.CONTAINERS:
                  containerColumnCells.push(containerCell);
                  break;
                case TableColumnType.PARENT_STORABLE:
                  containerColumnCells.push(locationCell);
                  break;
                case TableColumnType.POSITION:
                  containerColumnCells.push(positionCell);
                  break;
                case TableColumnType.TAG_VALUE: {
                  if (!sample) {
                    containerColumnCells.push({text: '', locked: true});
                    break;
                  }
                  const container = getContainerForSampleFromREST({sample, containersById});
                  const {schemaField: containerField} = columnDefinition;
                  containerColumnCells.push(renderContainerFieldCell(container, containerField));
                  break;
                }
                default:
                  throw new Error(`Unexpected column definition type ${columnDefinition.type}`);
              }
            }
            return containerColumnCells;
          })(),
        ];
      },
      renderRequestID: () => ({
        text: hydratedFulfillment.request.name,
        formattedValue: (
          <BenchLink
            href={pathToURL(RequestDAO.requestEditorURL(hydratedFulfillment.request.id))}
            fileId={hydratedFulfillment.request.id}
          >
            {hydratedFulfillment.request.name}
          </BenchLink>
        ),
        locked: true,
      }),
      renderTask: () => ({
        text: (hydratedFulfillment.requestTaskSchema && hydratedFulfillment.requestTaskSchema.name) || '',
        locked: true,
      }),
      renderStatus: () => ({
        text: '',
        typedValue: {
          type: BaseTypedValueType.BOOLEAN,
          booleanValue: hydratedFulfillment.fulfillment.statusAfterReview === FulfillmentStatus.FAILED,
        },
        locked,
      }),
      renderRequestField: (field: SchemaField, requestSchema: RequestSchema) => {
        const tag =
          requestSchema.id === hydratedFulfillment.requestSchema.id
            ? findSchemaTag(request.tags, field.id)
            : null;
        if (!tag) {
          return {text: '', locked: true};
        }
        return {text: '', typedValue: convertTagToTypedValue(tag), locked: true};
      },
      renderRequestTaskField: (field: SchemaField, taskSchema: RequestTaskSchema) => {
        FulfillmentTables.checkFulfillmentForRenderRequestTaskField(hydratedFulfillment);
        const tag =
          hydratedFulfillment.requestTaskSchema && taskSchema.id === hydratedFulfillment.requestTaskSchema.id
            ? findSchemaTag(hydratedFulfillment.requestTask!.tags, field.id)
            : null;
        if (!tag) {
          return {text: '', locked: true};
        }
        return {text: '', typedValue: convertTagToTypedValue(tag), locked: true};
      },
    });
  });
}

export function getColumnDefinitions(
  params: Omit<CreateRequestFulfillmentsTableParams, 'isFinalized'>,
  sampleFieldContainerColumns: {
    [sampleGroupFieldId: string]: Array<FulfillmentSpreadsheetColumn>;
  } = getSampleFieldContainerColumns(params)
): Array<ColumnDefinition | null> {
  const {hydratedFulfillments} = params;
  return FulfillmentTables.fulfillmentRow<ColumnDefinition | null>(hydratedFulfillments, {
    renderSampleGroupField: (sampleGroupField: SchemaField) => [
      {
        ...schemaFieldToColumnDefinition(sampleGroupField),
        type: TableColumnType.SAMPLE_GROUP_SAMPLE,
      },
      ...sampleFieldContainerColumns[sampleGroupField.apiId].map(({columnDefinition}) => columnDefinition),
    ],
    renderRequestID: () => null,
    renderTask: () => null,
    renderStatus: () => ({
      type: TableColumnType.BOOLEAN,
      isRequired: false,
      // This name is never actually used; getColumnHeaders handles column header naming instead.
      // This is a placeholder name to fulfill the BooleanColumnDefinition type contract.
      name: 'Status',
    }),
    renderRequestField: (requestField: SchemaField) => schemaFieldToColumnDefinition(requestField),
    renderRequestTaskField: (requestTaskField: SchemaField) =>
      schemaFieldToColumnDefinition(requestTaskField),
  });
}

function getColumnHeaders(
  hydratedFulfillments: Array<HydratedFulfillmentWithPreparedSampleGroup>,
  sampleFieldContainerColumns: {
    [sampleGroupFieldId: string]: Array<FulfillmentSpreadsheetColumn>;
  }
): Array<string> {
  return FulfillmentTables.headers(hydratedFulfillments, {
    renderCustomSampleGroupFieldHeaders: (field: SchemaField) => [
      field.displayName,
      ...sampleFieldContainerColumns[field.apiId].map(({renderedLabel}) => renderedLabel),
    ],
  });
}

/**
 * A couple of things to keep in mind when using this tool:
 *  - It doesn't have the table name. If you need the `name` field filled out, you're on your own.
 *  - The `rowData` array is set with finalized rows if `locked` is set to true.  This prevents removing rows
 */
export default function createRequestFulfillmentsTable(params: CreateRequestFulfillmentsTableParams): Table {
  const sampleFieldContainerColumns = getSampleFieldContainerColumns(params);
  const columnDefinitions = getColumnDefinitions(params);
  const headers = getColumnHeaders(params.hydratedFulfillments, sampleFieldContainerColumns);
  const rowData = params.hydratedFulfillments.map((hydratedFulfillment) => ({
    isFinalized: params.isFinalized,
    fulfillmentId: hydratedFulfillment.fulfillment.id,
  }));

  return {
    name: '',
    table: createTable(params),
    columnConstraints: columnDefinitions.map((columnDefinition) =>
      columnDefinition == null ? null : {columnDefinition}
    ),
    headerRow: false,
    headerCol: false,
    columnLabels: headers,
    widths: headers.map(() => 120),
    rowData,
    allowRowRemoval: !params.locked,
    allowNoRows: true,
    lockedDims: true,
    _hideRename: true,
  };
}
